"use strict";
/* eslint-disable no-restricted-syntax */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getEnvironmentConfig = void 0;
/** Shown Environment in Console? */
let shownEnv = false;
/**
 * Gets the Configuration for a Frontend Service.
 */
function getEnvironmentConfig() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const globals = window || global || {};
    // eslint-disable-next-line no-undef
    const environmentConfig = globals.environmentConfig || {};
    const getConfig = (name) => environmentConfig[name];
    const buildVersion = getConfig('BUILD_VERSION');
    const environment = getConfig('CRIMSON_ENV');
    const namespace = getConfig('NAMESPACE');
    const environmentInfo = {
        buildVersion,
        environment,
        namespace,
        localhost: getConfig('LOCALHOST') === 'true' || environment === 'local',
    };
    const isProduction = environment ? environment.endsWith('prod') || environment.startsWith('prod') : false;
    const isStaging = environment ? environment.endsWith('staging') || environment.startsWith('staging') : false;
    const isTest = environment === 'test';
    const isMultiEnvironment = namespace !== environment;
    const isDev = isTest || environmentInfo.localhost;
    // Log Environment Data if not Prod.
    if (!isProduction && !shownEnv) {
        // eslint-disable-next-line no-console
        console.log('Environment: ', environmentConfig);
        shownEnv = true;
    }
    const devOptions = {
        isProduction,
        isStaging,
        isTest,
        isMultiEnvironment,
        isDev,
        enableDevTools: !isProduction && getConfig('devTools') === 'true',
    };
    const region = getConfig('REGION');
    // Get Regional Config, and apply the name inside the object.
    const serviceConfig = JSON.parse(getConfig('SERVICE_CONFIG') || '{}');
    Object.keys(serviceConfig).forEach((regionName) => {
        const regional = serviceConfig[regionName];
        regional.name = regionName;
    });
    /**
     * Region based configuration
     */
    const regionInfo = {
        /** Configuration map for all regions. Key = Region name, Value = Region config */
        serviceConfig,
        /** The App's hosted Region. */
        /** The Area of the Region, e.g. Sydney, Shanghai. */
        regionZone: getConfig('REGION_ZONE'),
        /** Config for the App Region. */
        regionServiceConfig: serviceConfig[region] || {},
        /** Backwards compatibility china region check, please use region === 'china' instead. */
        china: region === 'china',
        /**
         * Is Dulwich Environment? Some services aren't accessible in China.
         */
        dulwich: getConfig('IS_DULWICH') === 'true',
    };
    /**
     * Gets the key of a service for config variables.
     * @param {string} service service name
     * @param {string} zone zone of service.
     */
    function getServiceKey(service, zone) {
        return (service || zone).toUpperCase().replace(/-/g, '_').replace(/\./g, '_');
    }
    /**
     * Resolves the Fully Qualified Domain Name Address of a Service.
     * @param {String} service The Name of the Service to get.
     * @param {String} zone The Zone type of Service this is, based on the keys of service_config.
     * @param {String} options.protocol The Protocol to use for the address.
     * Leave undefined to not attach a protocol to the address. HTTPS will be used by default for http.
     * @returns {string} Resolved Address
     */
    const getServiceAddress = (service, zone, options = {}) => {
        // Determine if address is locally routable, which is everything except for fringe services.
        const isLocallyRoutable = environmentInfo.localhost && zone !== 'fringe';
        let address;
        let path = '';
        let port;
        let { protocol } = options;
        let isSecure = true;
        if (isLocallyRoutable) {
            address = 'localhost';
            port = getConfig(`${getServiceKey(service, zone)}_INGRESS_PORT`);
            isSecure = false;
        }
        else {
            const zoneInfo = regionInfo.regionServiceConfig[zone];
            if (!zoneInfo) {
                throw new Error(`Can't resolve Zone info for Zone: ${zone}`);
            }
            address = `${zoneInfo.subdomain}.${zoneInfo.domain}`;
            address = service ? `${service}.${address}` : address;
            path = zoneInfo.path_prefix ? zoneInfo.path_prefix : '';
            address = zoneInfo.subdomain_prefix ? `${zoneInfo.subdomain_prefix}.${address}` : address;
            isSecure = true;
        }
        if (['https', 'http'].includes(protocol || '')) {
            protocol = isSecure ? 'https' : 'http';
        }
        if (['wss', 'ws'].includes(protocol || '')) {
            protocol = isSecure ? 'wss' : 'ws';
        }
        // Attach Protocol
        if (protocol) {
            address = `${protocol}://${address}`;
        }
        // Attach Port
        if (port) {
            address += `:${port}`;
        }
        if (path) {
            address += path;
        }
        // Sanity check, fail fast.
        if (!address || address.includes('undefined')) {
            throw new Error(`Can't resolve service address for ${service || '@'} in ${zone}. Got: ${address}`);
        }
        return address;
    };
    /**
     * Get Service Routes to reach a Service.
     * @param {string} service Service name to get routing info for.
     */
    function getServiceInfo(service) {
        return {
            endpoint: getServiceAddress(service, 'internal', {
                protocol: 'http',
            }),
        };
    }
    /**
     * Get Service Routes + Graphql to reach a Service.
     * @param {string} service Service name to get routing info for.
     */
    function getGraphQlServiceInfo(service) {
        const serviceInfo = getServiceInfo(service);
        return {
            ...serviceInfo,
            /** Graphql endpoint for this service. */
            graphQLEndpoint: `${serviceInfo.endpoint}/graphql`,
        };
    }
    /** Service Configuration */
    const services = {
        /** API Service Configuration and discovery information. */
        api: {
            ...getGraphQlServiceInfo('api'),
            websocketEndpoint: getServiceAddress('websocket', 'internal', { protocol: 'wss' }),
        },
    };
    const auth0 = {
        domain: getConfig('AUTH0_DOMAIN') || 'PLACEHOLDER_DOMAIN',
        clientID: getConfig('AUTH0_CLIENT_ID') || 'PLACEHOLDER_ID',
        audience: getConfig('AUTH0_API_AUDIENCE'),
        scope: 'openid email offline_access profile',
        responseType: 'code',
        redirectUri: `${services.api.endpoint}/authorize`,
        refreshAccessTokensUri: `${services.api.endpoint}/refreshAccessTokens`,
        signOutUri: `${services.api.endpoint}/signout`,
    };
    const firebase = {
        apiKey: 'AIzaSyB656tyDVKoEWE2FsDZu7t-UzC1mbxUQyQ',
        authDomain: 'crimsonlms.firebaseapp.com',
        databaseURL: 'https://crimsonlms.firebaseio.com',
        storageBucket: 'crimsonlms.appspot.com',
        messagingSenderId: '911916005476',
        rootPath: getConfig('FIREBASE_ROOT_PATH'),
        lessonPath: getConfig('FIREBASE_LESSON_PATH'),
        notificationPath: getConfig('FIREBASE_NOTIFICATION_PATH'),
    };
    const vapid = {
        publicKey: getConfig('CRIMSON_VAPID_PUBLIC_KEY'),
    };
    /** Push Notifications Config. */
    const pushNotifications = {
        /** The Accessible Endpoint for Notification Management. */
        endpoint: getServiceAddress('notifications', 'fringe', { protocol: 'https' }),
        websocketEndpoint: getServiceAddress('ws.notifications', 'fringe', { protocol: 'wss' }),
    };
    /** Tencent instant messaging config. */
    const timConfig = {
        sdkAppId: parseInt(getConfig('TIM_APP_ID') || '0', 10),
        source: getConfig('TIM_SOURCE'),
    };
    const googleCalendarOauth = {
        clientId: getConfig('GOOGLE_CALENDAR_CLIENT_ID'),
    };
    /** Intercom bubble config. */
    const intercom = {
        /** App Id for Intercom */
        appId: getConfig('INTERCOM_APP_ID'),
    };
    /**
     * Datadog Frontend Reporting
     */
    const datadog = {
        site: getConfig('DD_SITE'),
        applicationId: getConfig('DD_APPLICATION_ID'),
        clientToken: getConfig('DD_CLIENT_TOKEN'),
    };
    /**
     * Storyblok content develiery api
     */
    const storyblokConfig = {
        endpoint: getConfig('STORYBLOK_ENDPOINT'),
        token: getConfig('STORYBLOK_TOKEN'),
        version: getConfig('STORYBLOK_VERSION'),
    };
    const awsAmplifyConfig = {
        region: getConfig('AWS_AMPLIFY_REGION'),
        analyticsAPPId: getConfig('AWS_AMPLIFY_ANALYTICS_APP_ID'),
        identityPoolId: getConfig('AWS_AMPLIFY_IDENTITY_POOL_ID'),
        proxyUrl: getConfig('AWS_AMPLIFY_PROXY_URL'),
    };
    const launchDarkly = {
        clientSideId: getConfig('LAUNCH_DARKLY_CLIENT_SIDE_ID'),
    };
    const coreOmsConfig = {
        graphqlEndpoint: getConfig('CORE_OMS_GRAPHQL_ENDPOINT'),
    };
    return {
        ...environmentInfo,
        ...regionInfo,
        ...services,
        ...devOptions,
        auth0,
        datadog,
        firebase,
        vapid,
        timConfig,
        intercom,
        pushNotifications,
        googleCalendarOauth,
        getServiceAddress,
        storyblokConfig,
        awsAmplifyConfig,
        launchDarkly,
        coreOmsConfig,
    };
}
exports.getEnvironmentConfig = getEnvironmentConfig;
//# sourceMappingURL=index.js.map